import React, { ReactElement, ReactNode, useCallback, useEffect } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerProps,
  ControllerRenderProps,
  FieldValues,
  Path,
  PathValue,
  useFormContext,
} from 'react-hook-form';
import styled, { css } from 'styled-components';

import { Icon, Tooltip } from '@column/column-ui-kit';

import { HelpSidebarCustomType, useHelpSidebarStore } from '~/stores/HelpSidebar';

export type FormFieldProps<T extends FieldValues> = {
  id: Path<T>;
  label?: string;
  labelVariant?: 'default' | 'primary';
  description?: string;
  tooltip?: string | HelpSidebarCustomType;
  rules?: ControllerProps<T, Path<T>>['rules'];
  defaultValue?: PathValue<T, Path<T>> | undefined;
  children: (field: ControllerRenderProps<T, Path<T>>, fieldState: ControllerFieldState) => ReactElement | ReactNode;
  updateOn?: Path<T>;
  compact?: boolean;
  hide?: boolean;
  oneColumn?: boolean;
  noStyle?: boolean;
};

const Label = styled.div<{ $variant?: 'default' | 'primary' }>`
  font-size: 14px;
  line-height: 18px;
  font-weight: 500;
  color: ${({ theme, $variant }) => ($variant === 'primary' ? theme.primary.background : theme.foreground)};

  @container form-field (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    margin-top: 12px;
  }
`;

const Container = styled.div`
  container-type: inline-size;
  container-name: form-field;
`;

const ControllerContainer = styled.div`
  isolation: isolate;
  z-index: 1;
`;

const Wrapper = styled.div<{ $compact?: boolean; $oneColumn?: boolean; $noStyle?: boolean }>`
  ${({ $noStyle }) =>
    !$noStyle &&
    css`
      display: grid;
      grid-column-gap: 24px;
      align-items: start;
      grid-template-columns: auto;
      grid-template-rows: auto;
      grid-row-gap: 8px;

      > *:nth-child(3) {
        grid-column: 1 / -1;
      }

      @container form-field (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
        grid-template-columns: minmax(180px, 25%) 1fr;
        grid-template-rows: auto auto;
        grid-row-gap: 0px;

        > *:nth-child(3) {
          grid-column: 1 / -1;
        }
      }

      @container form-field (min-width: ${({ theme }) => theme.containerBreakpoints.wide}) {
        grid-template-columns: minmax(180px, 25%) auto 25%;
        grid-template-rows: auto;
        grid-row-gap: 0px;

        > *:nth-child(3) {
          grid-column: auto;
        }
      }
    `};

  ${({ $compact }) =>
    $compact &&
    css`
      grid-template-columns: fit-content(100%) fit-content(100%);
    `};

  ${({ $oneColumn }) =>
    $oneColumn &&
    css`
      @container form-field (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
        grid-gap: 8px;
        grid-template-columns: auto;

        ${Label} {
          margin-top: 0;
        }
      }
    `}
`;

const Title = styled.div`
  line-height: 1;
  display: grid;
  grid-gap: 0;

  @container form-field (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    grid-gap: 4px;
  }
`;

const LabelTooltip = styled(Tooltip)`
  display: inline-block;
  vertical-align: top;
  margin-left: 4px;

  svg {
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(500)};
    --icon-size: 16px;
  }

  &:hover {
    svg {
      --icon-color: ${({ theme }) => theme.secondary.blendToBackground(900)};
    }
  }
`;
const TooltipButton = styled.button`
  appearance: none;
  padding: 0;
  margin: 0;
  outline: none;
  border: none;
  cursor: pointer;
  position: relative;
  background-color: transparent;
`;

const LabelSidebarTooltip = styled(Tooltip)`
  display: inline-block;
  vertical-align: top;
  margin-left: 4px;
  position: relative;

  &:after {
    content: '';
    display: block;
    position: absolute;
    inset: 1.5px;
    border-radius: 100%;
    background-color: ${({ theme }) => theme.secondary.blendToForeground(1, 900)};
    opacity: 0;
    transition: opacity 0.1s;
    z-index: 0;
  }

  svg {
    --icon-size: 16px;
    transition: opacity 0.2s;
    z-index: 1;

    &:nth-child(1) {
      --icon-color: ${({ theme }) => theme.secondary.blendToBackground(500)};
    }

    &:nth-child(2) {
      --icon-color: ${({ theme }) => theme.background};
      position: absolute;
      top: 0;
      pointer-events: none;
      transform: translateX(4px);
      opacity: 0;
      transition:
        transform 0.1s,
        opacity 0.1s;
      border-radius: 100%;
    }
  }

  &:hover {
    cursor: pointer;

    &:after {
      opacity: 1;
      transition: opacity 0.2s;
    }

    svg {
      &:nth-child(1) {
        opacity: 0;
        transition: opacity 0.1s;
      }

      &:nth-child(2) {
        transform: translateX(0);
        opacity: 1;
        transition:
          transform 0.2s,
          opacity 0.2s;
      }
    }
  }
`;

const Asterix = styled.span`
  color: ${({ theme }) => theme.danger.background};
  font-weight: 700;
  display: inline-block;
  margin-left: 2px;
`;

const Description = styled.div`
  font-size: 12px;
  line-height: 18px;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  max-width: 100%;

  @container form-field (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    max-width: 32ch;
  }
`;

export const FormField = <T extends FieldValues>({
  id,
  label,
  labelVariant,
  rules,
  defaultValue,
  children,
  description,
  tooltip,
  updateOn,
  compact,
  oneColumn,
  noStyle,
  hide,
}: FormFieldProps<T>) => {
  const { control, watch, register } = useFormContext<T>();

  const openHelpSidebarCustom = useHelpSidebarStore((s) => s.openHelpSidebarCustom);

  const watchValue = updateOn ? watch(updateOn) : undefined;

  const handleTooltipClick = useCallback(() => {
    if (typeof tooltip === 'string' || typeof tooltip === 'undefined') {
      return;
    }

    openHelpSidebarCustom(tooltip);
  }, [tooltip]);

  useEffect(() => {
    if (!hide) {
      register(id);
    }
  }, [hide, id]);

  if (hide) {
    return null;
  }

  return (
    <Container>
      <Wrapper $compact={compact} $oneColumn={oneColumn} $noStyle={noStyle} className="form-field">
        {!noStyle && (
          <Title>
            <Label $variant={labelVariant}>
              {label}
              {!!rules?.required && <Asterix>*</Asterix>}

              {typeof tooltip === 'string' ? (
                <LabelTooltip content={tooltip} zIndex={14}>
                  <Icon.CircleQuestionmark />
                </LabelTooltip>
              ) : (
                typeof tooltip === 'object' && (
                  <TooltipButton type="button" onClick={handleTooltipClick}>
                    <LabelSidebarTooltip content="Click to learn more">
                      <Icon.CircleQuestionmark />
                      <Icon.ChevronLeft />
                    </LabelSidebarTooltip>
                  </TooltipButton>
                )
              )}
            </Label>
            {description && <Description>{description}</Description>}
          </Title>
        )}
        <ControllerContainer>
          <span data-cy={`form-field-${id}`}>
            <Controller
              key={watchValue}
              name={id}
              control={control}
              defaultValue={defaultValue}
              rules={{
                ...rules,
                required: rules?.required === true ? `Please fill out the ${label} field` : rules?.required,
              }}
              render={({ field, fieldState }) => {
                return children(field, fieldState) as ReactElement;
              }}
            />
          </span>
        </ControllerContainer>
      </Wrapper>
    </Container>
  );
};
